import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { supabase } from '../lib/supabase';
import AddToHomescreenPrompt from '../components/AddToHomescreenPrompt';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [nameShort, setNameShort] = useState<string>('');
  const [isMale, setIsMale] = useState<boolean>(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const detection = useDeviceDetection();

  const handleContinue = useCallback(() => {
    if (detection.canInstall && !detection.isStandalone) {
      setShowInstallPrompt(true);
    } else {
      navigate('/');
    }
  }, [detection.canInstall, detection.isStandalone, navigate]);

  const handleInstallDismiss = useCallback(() => {
    setShowInstallPrompt(false);
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('name_short, name_sex_male')
          .eq('id', user.id)
          .single();
        if (profile) {
          setNameShort((profile as any).name_short || '');
          const male = (profile as any)?.name_sex_male === null ? true : !!(profile as any)?.name_sex_male;
          setIsMale(male);
        }
      } catch {}
    })();
  }, []);

  return (
    <div className="fixed inset-0 z-40 md:static md:z-auto w-full md:min-h-screen md:flex md:items-center md:justify-center bg-surface-card md:bg-transparent overflow-hidden md:overflow-visible">
      <div className="relative bg-surface-card w-full h-full md:h-auto px-4 md:px-8 py-10 md:py-12 md:max-w-4xl md:mx-auto md:rounded-lg md:border md:border-neutral-200 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto mb-4 w-24 h-24">
            <DotLottieReact
              src="/images/lottie_animations/Success.lottie"
              autoplay
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          <h1 className="text-[17px] font-semibold text-brand-black/90 mb-2">Subscripción exitosa.</h1>
          <p className="text-[15px] text-neutral-600">
            {`${nameShort ? `${nameShort}, ` : ''}${isMale ? 'bienvenido' : 'bienvenida'} a la revolución de la IA.`}
          </p>
          {/* Desktop inline helper + button */}
          <div className="hidden md:block mt-8">
            <p className="text-[13px] text-neutral-400 mb-3">Ante cualquier duda te podes comunicar con nostros yendo a la pestaña de Ayuda.</p>
            <button
              onClick={handleContinue}
              className="inline-flex items-center justify-center h-10 px-5 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
      {/* Mobile fixed bottom actions. El fondo blanco de la barra debe pintar a
          través de env(safe-area-inset-bottom) (zona del home indicator en PWA
          standalone / iOS con notch); sin esto asoma el #F2F2F7 del <html>. */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 bg-surface-card px-4 pt-4 border-t border-neutral-200"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <p className="text-[13px] text-neutral-400 mb-2 text-center">Ante cualquier duda te podes comunicar con nostros yendo a la pestaña de ayuda.</p>
        <button
          onClick={handleContinue}
          className="w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
        >
          Continuar
        </button>
      </div>
      {showInstallPrompt && (
        <AddToHomescreenPrompt
          appName="Atlas"
          appIconUrl="/favicon.png"
          delayMs={0}
          forceShow
          onDismiss={handleInstallDismiss}
          onInstall={handleInstallDismiss}
        />
      )}
    </div>
  );
};

export default SubscriptionSuccess;
